import useComponentSize from "@rehooks/component-size"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import Carousel from "react-gallery-carousel"
import { PostListItemExpandedStyles, PostListItemStyles } from "../posts/constants"
import styles from "./styles.module.scss"
import { isMobile } from "react-device-detect"
import { useQueryClient } from "react-query"
import { Spinner } from "../shared/Spinner"

// How often to poll while a video attachment is still processing on Mux.
// Posts have no push mechanism (unlike comments, which get an ActionCable
// broadcast from the Mux webhook), so this just re-fetches the post list
// periodically until the video is ready.
const PROCESSING_POLL_INTERVAL_MS = 8000

interface Media {
  filename: string
  url: string | null
  content_type_generic: string
  content_type_specific: string
  poster_url?: string | null
  video_ready?: boolean
  isProcessing?: boolean
  signed_id?: string
}

// poster_url comes from Mux itself, available as soon as the asset's
// playback_id is set - well before the video is actually transcoded/
// playable (see MuxAsset#poster_url) - so this often has a real frame from
// the video, not just a generic placeholder, even while still "processing".
const ProcessingVideoSlide: React.FC<{ media: Media }> = ({ media }) => {
  const posterUrl = media.poster_url
  return (
    <div
      className={styles.processingContainer}
      style={
        posterUrl
          ? {backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center"}
          : undefined
      }
    >
      <div className={posterUrl ? styles.processingScrim : undefined}>
        <Spinner className={styles.processingSpinner} />
        <div className={styles.processingText}>
          Processing video…{" "}
          <span className={styles.filename}>{media.filename}</span>
          <div className={styles.smallProcessingText}>
            Check back soon
          </div>
        </div>
      </div>
    </div>
  )
}

interface ImageVideoAlbumPlayerProps {
  attachedMedia: Media[]
  expanded?: boolean
}

const ImageVideoAlbumPlayer: React.FC<ImageVideoAlbumPlayerProps> = ({
  attachedMedia,
  expanded = false,
}) => {
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  const ref = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  let size = useComponentSize(ref)

  useLayoutEffect(() => {
    setWidth(size.width)
    if (isMobile) {
      setHeight((width * 9) / 16)
    } else {
      expanded
        ? setHeight(width ? (width * 2) / 3 : 0)
        : setHeight(width ? (width * 9) / 16 : 0)
    }
  }, [size.width, width, expanded])

  const headlineStyle: React.CSSProperties = {
    height: height,
  }
  const contentDivStyles = expanded
    ? PostListItemExpandedStyles.contentDiv
    : PostListItemStyles.contentDiv

  const playableMedia = attachedMedia.filter(
    (media) => media.content_type_generic === "image" || media.video_ready,
  )
  const processingMedia = attachedMedia
    .filter((media) => media.content_type_generic === "video" && !media.video_ready)
    .map((media) => ({ ...media, isProcessing: true }))
  const allMedia = [...playableMedia, ...processingMedia]

  const queryClient = useQueryClient()
  useEffect(() => {
    if (processingMedia.length === 0) return

    const interval = setInterval(() => {
      queryClient.invalidateQueries(["posts"])
    }, PROCESSING_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [processingMedia.length, queryClient])

  const handleCarouselSwipe = () => {
    videoRef.current?.pause()
  }

  return (
    <div
      ref={ref}
      style={headlineStyle}
      className={contentDivStyles + " w-full "}
    >
      <Carousel
        canAutoPlay={false}
        hasThumbnails={false}
        className={styles.carousel}
        style={{
          backgroundColor: "black",
          alignItems: "center",
          color: "white",
        }}
        hasIndexBoard="bottomRight"
        onIndexChange={handleCarouselSwipe}
      >
        {allMedia.map((media) =>
          media.isProcessing ? (
            <ProcessingVideoSlide key={media.filename} media={media} />
          ) : media.content_type_generic === "video" ? (
            media.video_ready ? (
              <div className={styles.videoContainer} key={media.filename}>
                <video
                  ref={videoRef}
                  className={styles.video}
                  poster={media.poster_url ?? undefined}
                  preload="metadata"
                  controls
                >
                  <source src={media.url ?? undefined} type="video/mp4" />
                </video>
              </div>
            ) : null
          ) : (
            <div className={styles.imgContainer} key={media.filename}>
              <img
                src={media.url ?? undefined}
                alt={media.filename}
                title={media.filename}
              />
            </div>
          ),
        )}
      </Carousel>
    </div>
  )
}

export default ImageVideoAlbumPlayer

